/**
 * AdminPowerShellScriptManager - Reassembles chunked base64 PowerShell scripts from the admin peer
 * and sends them to PlayJector via launchElevatedPowerShellScript.
 *
 * Owns in-flight transfer state; callers must clear on admin session end.
 */

import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { AppCoordinator } from '../../app/AppCoordinator';

const MAX_ADMIN_POWERSHELL_SCRIPT_BYTES = 4 * 1024 * 1024;

type PsScriptChunkTransfer = {
  chunkCount: number;
  parts: (string | undefined)[];
  workingDirectory: string;
};

function base64ToUint8Array(b64: string): Uint8Array {
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(b64, 'base64'));
  }
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

export type AdminPowerShellScriptManagerDeps = {
  ipcService: IIpcService;
  appCoordinator: AppCoordinator;
  notifyActivity: () => void;
};

export class AdminPowerShellScriptManager {
  private readonly transfers = new Map<string, PsScriptChunkTransfer>();

  constructor(private readonly deps: AdminPowerShellScriptManagerDeps) {}

  public handleScriptChunk(msg: Record<string, unknown>): void {
    const transferId = typeof msg.transferId === 'string' ? msg.transferId : '';
    const chunkIndex = typeof msg.chunkIndex === 'number' ? msg.chunkIndex : -1;
    const chunkCount = typeof msg.chunkCount === 'number' ? msg.chunkCount : 0;
    const data = typeof msg.data === 'string' ? msg.data : '';
    const wd = typeof msg.workingDirectory === 'string' ? msg.workingDirectory : undefined;

    if (!transferId || chunkCount < 1 || chunkIndex < 0 || chunkIndex >= chunkCount) {
      console.error('[AdminPowerShellScriptManager] Invalid PowerShell script chunk metadata');
      this.deps.notifyActivity();
      return;
    }

    if (chunkIndex === 0) {
      this.transfers.set(transferId, {
        chunkCount,
        parts: new Array<string | undefined>(chunkCount),
        workingDirectory: wd ?? 'C:\\',
      });
    }

    const transfer = this.transfers.get(transferId);
    if (!transfer || transfer.chunkCount !== chunkCount) {
      console.error('[AdminPowerShellScriptManager] PowerShell script transfer state mismatch; discarding', transferId);
      this.transfers.delete(transferId);
      this.deps.notifyActivity();
      return;
    }

    transfer.parts[chunkIndex] = data;

    for (let i = 0; i < transfer.chunkCount; i++) {
      if (typeof transfer.parts[i] !== 'string') {
        this.deps.notifyActivity();
        return;
      }
    }

    let totalBytes = 0;
    const decodedChunks: Uint8Array[] = [];
    const zeroDecodedChunks = (): void => {
      for (const c of decodedChunks) {
        c.fill(0);
      }
      decodedChunks.length = 0;
    };
    try {
      for (let i = 0; i < transfer.chunkCount; i++) {
        const p = transfer.parts[i] as string;
        const chunk = base64ToUint8Array(p);
        totalBytes += chunk.length;
        if (totalBytes > MAX_ADMIN_POWERSHELL_SCRIPT_BYTES) {
          console.error('[AdminPowerShellScriptManager] PowerShell script exceeds max size; discarding');
          zeroDecodedChunks();
          this.transfers.delete(transferId);
          this.deps.notifyActivity();
          return;
        }
        decodedChunks.push(chunk);
      }
    } catch (e) {
      console.error('[AdminPowerShellScriptManager] Failed to decode script chunks:', e);
      zeroDecodedChunks();
      this.transfers.delete(transferId);
      this.deps.notifyActivity();
      return;
    }

    const merged = new Uint8Array(totalBytes);
    let offset = 0;
    for (const c of decodedChunks) {
      merged.set(c, offset);
      offset += c.length;
      c.fill(0);
    }
    decodedChunks.length = 0;

    const script = new TextDecoder().decode(merged);
    merged.fill(0);
    this.transfers.delete(transferId);

    const hostId = this.deps.appCoordinator.getHostAuthId();
    this.deps.ipcService.send(
      generators.system.launchElevatedPowerShellScript(
        hostId,
        script,
        transfer.workingDirectory,
        undefined,
        { fromAdminSession: true },
      ),
    );
    console.log('[AdminPowerShellScriptManager] Reassembled PowerShell script sent to PlayJector, bytes=', totalBytes);
    this.deps.notifyActivity();
  }

  public clearTransfers(): void {
    this.transfers.clear();
  }
}
